/**
 * css-id-rewriter.js — AST-located, byte-preserving id rewriting for export
 * CSS (Workstream A3).
 *
 * The legacy regex path (`rewriteTemplateSelectorsForSection`) rewrote
 * `#id` / `[data-pb-section="id"]` textually, which cannot distinguish a
 * selector from the inside of a `content: "#id"` string or an escaped
 * selector. This module parses with css-tree (`positions: true`), collects
 * the exact source ranges of the nodes to change — `IdSelector`,
 * `[data-pb-section=…]` / `[id=…]` AttributeSelectors, and `url(#…)`
 * declaration values — and splices replacements into the ORIGINAL text in
 * reverse offset order. Every byte outside a replaced token is preserved,
 * so output is byte-identical to the regex path wherever the regex was
 * right, and correct where it was not.
 *
 * On a css-tree parse failure the caller's fallback (the legacy regex) is
 * used — signalled by returning null, never by throwing mid-export.
 *
 * Runs in browser (vendored css-tree, same import generators.js uses) and
 * Node alike.
 */

import * as csstree from '../vendor/csstree.esm.js';

function pushEdit(edits, loc, text) {
  if (!loc) return;
  edits.push({ start: loc.start.offset, end: loc.end.offset, text });
}

/**
 * Rewrite ids in CSS text.
 *
 * @param {string} css
 * @param {object} maps
 * @param {Record<string,string>} [maps.idSelectorMap] `#old` IdSelector →
 *   replacement SELECTOR TEXT (may be `:is(#a, #b)` fan-out).
 * @param {Record<string,string>} [maps.attrSectionMap]
 *   `[data-pb-section="old"]` → replacement selector text.
 * @param {Record<string,string>} [maps.idAttrMap] `[id="old"]` → new ID
 *   (attribute value replaced, selector shape kept).
 * @param {Record<string,string>} [maps.urlIdMap] `url(#old)` → new ID.
 * @returns {string|null} rewritten CSS, or null when css-tree cannot parse
 *   (caller falls back to its legacy path).
 */
export function rewriteCssIds(css, maps = {}) {
  const idSelectorMap = maps.idSelectorMap || {};
  const attrSectionMap = maps.attrSectionMap || {};
  const idAttrMap = maps.idAttrMap || {};
  const urlIdMap = maps.urlIdMap || {};

  let ast;
  try {
    ast = csstree.parse(css, { positions: true });
  } catch {
    return null;
  }

  const edits = [];

  csstree.walk(ast, (node) => {
    if (node.type === 'IdSelector') {
      const replacement = idSelectorMap[node.name];
      if (replacement !== undefined) pushEdit(edits, node.loc, replacement);
      return;
    }
    if (node.type === 'AttributeSelector' && node.value) {
      const attrName = node.name?.name;
      const value =
        node.value.type === 'String' ? node.value.value : (node.value.name ?? node.value.value);
      if (attrName === 'data-pb-section' && attrSectionMap[value] !== undefined) {
        pushEdit(edits, node.loc, attrSectionMap[value]);
      } else if (attrName === 'id' && idAttrMap[value] !== undefined) {
        pushEdit(edits, node.loc, `[id="${idAttrMap[value]}"]`);
      }
      return;
    }
    if (node.type === 'Url') {
      const raw = typeof node.value === 'string' ? node.value : node.value?.value;
      if (typeof raw !== 'string' || !raw.startsWith('#')) return;
      const newId = urlIdMap[raw.slice(1)];
      if (newId !== undefined) pushEdit(edits, node.loc, `url(#${newId})`);
    }
  });

  if (edits.length === 0) return css;

  // Overlapping edits cannot happen (distinct AST nodes), but sort + splice
  // in reverse so earlier offsets stay valid.
  edits.sort((a, b) => b.start - a.start);
  let out = css;
  for (const { start, end, text } of edits) {
    out = out.slice(0, start) + text + out.slice(end);
  }
  return out;
}

/**
 * Per-instance descendant-rename form: `#old` → `#new`, `[id="old"]` →
 * `[id="new"]`, `url(#old)` → `url(#new)`. `renameMap` is a Map(old → new).
 * Falls back to null on parse failure like rewriteCssIds.
 */
export function rewriteOwnedIdSelectors(css, renameMap) {
  if (!(renameMap instanceof Map) || renameMap.size === 0) return css;
  const idSelectorMap = {};
  const idAttrMap = {};
  const urlIdMap = {};
  for (const [oldId, newId] of renameMap) {
    idSelectorMap[oldId] = `#${newId}`;
    idAttrMap[oldId] = newId;
    urlIdMap[oldId] = newId;
  }
  return rewriteCssIds(css, { idSelectorMap, idAttrMap, urlIdMap });
}
